/**
 * Hook to manage focus navigation within the header menu.
 * Handles navigation between header items (room menu, game info, game close, etc.)
 * and provides focus state management for header buttons.
 */
import { useEffect, useRef, useState, useCallback } from 'react'
import { useFocusNavigationDep } from '../components/providers/FocusNavigationProvider/FocusNavigationProvider'
import { useSignals } from '@preact/signals-react/runtime'

export type HeaderItemType =
  | 'room-menu'
  | 'share-button'
  | 'leave-button'
  | 'gameless-button'
  | 'debug-toggle'
  | 'game-hints'
  | 'game-rulebook'
  | 'game-info'
  | 'game-configure'
  | 'game-close'
  | 'play-again'
  | 'pause-button'
  | 'theme-selector'
  | 'profile-avatar'

interface HeaderFocusNavigationOptions {
  /** Whether room menu is available */
  hasRoomMenu?: boolean
  /** Whether game controls are available (game is active) */
  hasGameControls?: boolean
  /** Whether this client may restart, configure, or close the game (see managed mode) */
  canControlGames?: boolean
  /** Whether play again button is visible */
  hasPlayAgain?: boolean
  /** Whether hints button is visible */
  hasHints?: boolean
  /** Whether rulebook button is visible */
  hasRulebook?: boolean
  /** Whether configure button is visible */
  hasConfigure?: boolean
  /** Whether pause button is visible */
  hasPause?: boolean
}

/**
 * Hook to handle focus navigation within the header.
 * Manages which header item is currently focused and handles keyboard navigation.
 */
export function useHeaderFocusNavigation(options: HeaderFocusNavigationOptions = {}) {
  useSignals()
  const focusNavigation = useFocusNavigationDep()
  const isActive = focusNavigation.isActive.value
  const focusArea = focusNavigation.focusArea.value
  const [focusedItem, setFocusedItem] = useState<HeaderItemType | null>(null)
  const itemRefs = useRef<Map<HeaderItemType, HTMLElement | null>>(new Map())

  // Build list of available header items in order.
  // Order (right arrow): close game -> room menu/share/leave -> theme -> profile -> play again -> hints -> rulebook -> game info -> configure -> pause -> wrap to close
  const availableItems = useRef<HeaderItemType[]>([])
  
  useEffect(() => {
    const items: HeaderItemType[] = []
    
    // 1. Close game first when game is active (consistent starting point)
    if (options.hasGameControls && options.canControlGames) {
      items.push('game-close')
    }
    
    // 2. Room menu (+ duplicates: share, leave), theme selector, profile-avatar last
    if (options.hasRoomMenu) {
      items.push('room-menu')
      items.push('share-button')
      items.push('leave-button')
    }
    items.push('gameless-button')
    items.push('theme-selector')
    items.push('debug-toggle')
    items.push('profile-avatar')
    
    // 3. Play again (when game over), game info, configure, pause
    if (options.hasGameControls) {
      if (options.hasPlayAgain && options.canControlGames) {
        items.push('play-again')
      }
      if (options.hasHints) {
        items.push('game-hints')
      }
      if (options.hasRulebook) {
        items.push('game-rulebook')
      }
      items.push('game-info')
      if (options.hasConfigure && options.canControlGames) {
        items.push('game-configure')
      }
      if (options.hasPause) {
        items.push('pause-button')
      }
    }
    
    availableItems.current = items
  }, [options.hasRoomMenu, options.hasGameControls, options.canControlGames, options.hasPlayAgain, options.hasHints, options.hasRulebook, options.hasConfigure, options.hasPause])

  // Reset focus when focus area changes or navigation becomes inactive
  useEffect(() => {
    if (!isActive || focusArea !== 'header') {
      setFocusedItem(null)
      return
    }
    // When switching to header, focus the first item
    if (availableItems.current.length > 0 && focusedItem === null) {
      setFocusedItem(availableItems.current[0])
    }
  }, [isActive, focusArea, focusedItem])

  const handleNavigate = useCallback((direction: 'left' | 'right') => {
    if (focusArea !== 'header' || !isActive) return

    setFocusedItem((current) => {
      if (current === null) {
        return availableItems.current[0] || null
      }

      const currentIndex = availableItems.current.indexOf(current)
      if (currentIndex === -1) {
        return availableItems.current[0] || null
      }

      let nextIndex: number
      if (direction === 'left') {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : availableItems.current.length - 1
      } else {
        nextIndex = currentIndex < availableItems.current.length - 1 ? currentIndex + 1 : 0
      }

      return availableItems.current[nextIndex] || null
    })
  }, [focusArea, isActive])

  const handleSelect = useCallback(() => {
    if (focusArea !== 'header' || !isActive || !focusedItem) return

    const element = itemRefs.current.get(focusedItem)
    if (!element) return

    // Radix triggers open on different events depending on the primitive: Dialog
    // (room menu, share, info/close-confirm) opens via onClick, but DropdownMenu
    // (profile avatar) only opens via onPointerDown/onKeyDown - it has no onClick
    // handler at all. Dispatch both so either kind of trigger responds. The keydown
    // is tagged so our own global keydown listener (controllerInput) doesn't loop
    // back and reprocess it as another semantic 'select' event.
    const syntheticKeydown = new KeyboardEvent('keydown', { key: 'Enter', bubbles: true, cancelable: true })
    Object.defineProperty(syntheticKeydown, 'merelsSynthetic', { value: true })
    element.dispatchEvent(syntheticKeydown)
    element.click()

    // Radix mounts dialog/menu content asynchronously after the state update that
    // opens it - checking synchronously here would always see "nothing open yet"
    // and switch focus to game content, which then steals it back via the game
    // grid's own list navigation and dismisses the overlay before it ever appears.
    requestAnimationFrame(() => {
      const openedOverlay = document.querySelector('[role="dialog"], [role="menu"]')
      if (!openedOverlay) {
        focusNavigation.setFocusArea('content')
      }
    })
  }, [focusArea, isActive, focusedItem, focusNavigation])

  // Focus the element when focusedItem changes
  useEffect(() => {
    if (isActive && focusArea === 'header' && focusedItem) {
      // Small delay to ensure element is ready and registered (especially for dynamically shown buttons like Play Again)
      const timeout = setTimeout(() => {
        const element = itemRefs.current.get(focusedItem)
        if (element) {
          element.focus()
        } else {
          // If element not found, retry after a short delay (handles registration timing)
          const retryTimeout = setTimeout(() => {
            const retryElement = itemRefs.current.get(focusedItem)
            if (retryElement) {
              retryElement.focus()
            }
          }, 50)
          return () => clearTimeout(retryTimeout)
        }
      }, 0)
      return () => clearTimeout(timeout)
    }
  }, [isActive, focusArea, focusedItem])

  const registerItem = useCallback((itemType: HeaderItemType, element: HTMLElement | null) => {
    if (element) {
      itemRefs.current.set(itemType, element)
    } else {
      itemRefs.current.delete(itemType)
    }
  }, [])

  // Stable ref callbacks - each item type gets a callback that doesn't change between renders
  const refCallbacks = useRef(new Map<HeaderItemType, (el: HTMLElement | null) => void>())

  const getRefCallback = useCallback((itemType: HeaderItemType) => {
    if (!refCallbacks.current.has(itemType)) {
      refCallbacks.current.set(itemType, (el: HTMLElement | null) => {
        registerItem(itemType, el)
      })
    }
    return refCallbacks.current.get(itemType)!
  }, [registerItem])

  return {
    focusedItem,
    registerItem,
    getRefCallback,
    handleNavigate,
    handleSelect,
  }
}
